import { config } from "@/utils/config";
import { computed, nextTick, Ref, ref } from "vue";
import {  InstantSearchOptions, Middleware, RenderState, UiState } from "instantsearch.js";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import { history } from "instantsearch.js/es/lib/routers";
import { getNewDocumentTitle } from "@/utils/meta";
import { singleIndex } from "instantsearch.js/es/lib/stateMappings";
import type { PlainSearchParameters } from "algoliasearch-helper";
import type {
    InfiniteHitsConnectorParams,
    InfiniteHitsRenderState
} from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
import type {
    HierarchicalMenuConnectorParams, HierarchicalMenuItem
} from "instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu";
import {
    CurrentRefinementsConnectorParams,
    CurrentRefinementsConnectorParamsItem,
    CurrentRefinementsConnectorParamsRefinement
} from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import {
    ClearRefinementsConnectorParams
} from "instantsearch.js/es/connectors/clear-refinements/connectClearRefinements";
import { RefinementListConnectorParams } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { MenuConnectorParams } from "instantsearch.js/es/connectors/menu/connectMenu";
import { RangeConnectorParams } from "instantsearch.js/es/connectors/range/connectRange";
import { SortByConnectorParams } from "instantsearch.js/es/connectors/sort-by/connectSortBy";
import { route } from 'ziggy-js';
import { getPageParams } from "@/vue/composables/useProductList/customHitsPerPage";
import { connectCurrentRefinements, connectSearchBox } from "instantsearch.js/es/connectors";
import { Livewire } from 'livewire';
import { httpResponseErrorHandler } from 'meilisearch'
import { MultiSearchParams } from "meilisearch/src/types/types";
import { useSaveScroll } from "@/vue/composables/useProductList/useSaveScroll";

export type ProductColor = {
    id: number,
    label: string,
    hex_value: string,
    is_light: boolean,
}

export type ProductListProps = {
    categoryGroup?: { id: number, url: string, name: string },
    categoryPage?: boolean,
    categoryPageRootTitle?: string,
    categories?: Array<{
        id: number,
        name: string,
        url: string,
        menuProduct?: { thumbnail: string },
        order: number,
        children: Array<{ id: number, name: string, url: string, order: number, menuProduct?: { thumbnail: string } }>
    }>,
    productColors: Array<ProductColor>,
    profileStore: { id: number, name: string } | null,
    catalogBlock?: { id: number },
    recipe?: { id: number },
    searchQuery?: string,
    filterSales?: boolean,
    filterBrandId?: string,
    filterProductTagId?: string,
    catalog?: { color: string | null, themes: Array<{ label: string, productTag: { id: number } }> },
}

export type InfiniteHitsState = Omit<InfiniteHitsRenderState, 'showMore' | 'hits'> & { refineNext: InfiniteHitsRenderState['showMore'] }

type UseProductListOptions = {
    hitsPerPageFirst: number,
    hitsPerPage: number,
    categoryDropdownFilter?: boolean,
}

export function useProductList(props: ProductListProps, { hitsPerPageFirst, hitsPerPage, categoryDropdownFilter }: UseProductListOptions) {
    const indexName = config('scout.prefix') + 'products';
    const filterInStockForProfileStore = ref(false);
    const renderState = ref<RenderState[string]>();
    const ready = ref(false);

    const { restoreScrollOnce } = useSaveScroll();

    async function onProductItemsLoaded() {
        // ready.value = true;
        await nextTick();
        restoreScrollOnce();
    }
    const flattenCategories = computed(() => props.categories?.flatMap(category => [
        category, ...category.children
    ]));
    function findCategory(idTrail: string|string[]) {
        if(typeof idTrail === 'string') {
            idTrail = idTrail.split(' > ');
        }
        const id = idTrail.at(-1);
        return flattenCategories.value?.find(category => String(category.id) === id);
    }

    function findColor(hexValue: string) {
        return props.productColors.find(color => color.hex_value === hexValue);
    }

    const instantSearchOptions: InstantSearchOptions<UiState, UiState[string]> & { middlewares?: Array<Middleware> } = {
        searchClient: (() => {
            const { searchClient } = instantMeiliSearch(
                config('scout.meilisearch.host'),
                config('scout.meilisearch.search_only_key'),
                {
                    finitePagination: true,
                    httpClient(input, init) {
                        if(typeof init?.body === 'string') {
                            const data: MultiSearchParams = JSON.parse(init.body);
                            if(data.queries) {
                                data.queries = data.queries.map(q => ({ ...q, ...getPageParams({ page: q.page!, hitsPerPage, hitsPerPageFirst }) }));
                                init.body = JSON.stringify(data);
                            }
                        }
                        return fetch(input, init).then(httpResponseErrorHandler).then(r => r.json().catch(() => undefined));
                    }
                }
            )
            return {
                ...searchClient,
                search(queries, options) {
                    return searchClient.search(queries, options);
                }
            }
        })(),
        indexName,
        future: {
            preserveSharedStateOnUnmount: true
        },
        middlewares: [
            function syncCategoryPage({ instantSearchInstance }) {
                document.addEventListener('update-category-trail', (e) => {
                    const uiState = instantSearchInstance.getUiState();
                    const trail = (e as CustomEvent).detail.trail;
                    if(trail.toString() !== (uiState[indexName].hierarchicalMenu?.category?.toString() ?? '')) {
                        instantSearchInstance.setUiState(uiState => {
                            uiState[indexName].hierarchicalMenu = {
                                category: trail.length ? trail : undefined,
                            }
                            return uiState;
                        });
                    }
                });
                return {
                    onStateChange({ uiState }) {
                        document.dispatchEvent(new CustomEvent('update-category-trail', {
                            detail: { trail: uiState[indexName].hierarchicalMenu?.category ?? [] }
                        }));
                    },
                }
            },
            function ({ instantSearchInstance }) {
                return {
                    subscribe() {
                        instantSearchInstance.addWidgets([
                            // customHitsPerPage({ hitsPerPage, hitsPerPageFirst }), // Not working for now, must do client override
                            connectSearchBox(() => {})({}),
                        ]);
                        instantSearchInstance.on('render', () => {
                            renderState.value = instantSearchInstance.renderState[indexName];
                            ready.value = !!instantSearchInstance.renderState[indexName].infiniteHits?.results;
                        });
                    },
                }
            },
            function handleEmptySearches({ instantSearchInstance }) {
                let recorded = false;
                return {
                    subscribe() {
                        instantSearchInstance.addWidgets([
                            connectCurrentRefinements((state) => {
                                const results = instantSearchInstance.mainIndex.getResults();
                                if(recorded) return;
                                if(results && !recorded) {
                                    if(state.items.length === 0
                                        && results.page === 0
                                        && results.query.length > 2
                                        && results.nbHits === 0
                                        && !filterInStockForProfileStore.value
                                    ) {
                                        Livewire.dispatch('no-results');
                                    }
                                    recorded = true;
                                }
                            })({ excludedAttributes: ['query'] }),
                        ])
                    },
                }
            }
        ],
        routing: {
            router: props.categoryPage ? history<UiState[string]>({
                cleanUrlOnDispose: true,
                windowTitle(state: UiState[string]) {
                    if(state.hierarchicalMenu?.category) {
                        const category = findCategory(state.hierarchicalMenu.category);
                        return category ? getNewDocumentTitle(category.name) : '';
                    }
                    return getNewDocumentTitle(props.categoryPageRootTitle!);
                },

                parseURL({ qsModule, location }): UiState[string] {
                    const state = qsModule.parse(location.search, { ignoreQueryPrefix: true });
                    const [_,categoryId,subCategoryId] = location.pathname.match(/[\w-]+\.(\d+)\/?(?:[\w-]+\.(\d+))?$/) ?? [];
                    // const categoryId = route().params.category;
                    // const subCategoryId = route().params.subCategory;
                    if(categoryId) {
                        return {
                            ...state,
                            hierarchicalMenu: categoryId ? {
                                ...(state.hierarchicalMenu as {}),
                                category: [
                                    categoryId,
                                    subCategoryId,
                                ].filter(c => typeof c !== 'undefined'),
                            } : undefined,
                        }
                    }
                    return state;
                },
                createURL({ qsModule, routeState, location }) {
                    const query = qsModule.stringify({
                        ...routeState,
                        page: undefined,
                        hierarchicalMenu: {
                            ...routeState?.menu,
                            category: undefined,
                        }
                    }, { addQueryPrefix: true });
                    if(routeState.hierarchicalMenu?.category) {
                        const category = findCategory(routeState.hierarchicalMenu.category);
                        return category ? category.url + query : location.href;
                    }
                    return props.categoryGroup
                        ? props.categoryGroup.url + query
                        : route('categories.index') + query;
                },
            }) : history({
                createURL({ qsModule, routeState, location }) {
                    const query = qsModule.stringify({
                        ...routeState,
                        page: undefined,
                    }, { addQueryPrefix: true });
                    return location.origin + location.pathname + query;
                }
            }),
            stateMapping: singleIndex(indexName),
        },
        // routing: false,
    }
    const searchParameters = computed<PlainSearchParameters>(() => {
        const filters = [];
        if(props.profileStore) {
            filters.push(`(filter_is_local=false OR filter_local_stores=${props.profileStore.id})`);
            if(filterInStockForProfileStore.value) {
                filters.push(`filter_stores_stocks.${props.profileStore.id}>0`);
            } else {
                filters.push(`(filter_is_sfs_available=true OR filter_stores_stocks.${props.profileStore.id}>0)`);
            }
            if(props.filterSales) {
                filters.push(`filter_in_sales_stores=${props.profileStore.id}`);
            }
        } else {
            filters.push('filter_is_local=false');
        }
        if(props.filterProductTagId) {
            filters.push(`filter_tags="${props.filterProductTagId}"`);
        }
        if(props.filterBrandId) {
            filters.push(`filter_brand=${props.filterBrandId}`);
        }
        if(props.categoryGroup?.id) {
            filters.push(`filter_category_group=${props.categoryGroup.id}`);
        }
        return {
            filters: filters.filter(Boolean).join(' AND ').replace(/\s+/g, ' '),
            /** handled in
             * @see customHitsPerPage */ // (not anymore)
            hitsPerPage,
        }
    });
    const infiniteHitsParams: InfiniteHitsConnectorParams = {
        // cache: createInfiniteHitsCache(),
        cache: createInfiniteHitsSessionStorageCache()
    }
    const categoryMenuParams: HierarchicalMenuConnectorParams = {
        attributes: ['category', 'subcategory_trail'],
        sortBy(a, b) {
            const cat1 = findCategory(a.name);
            const cat2 = findCategory(b.name);
            if(cat1?.order == null && cat2?.order == null) {
                return (cat1?.id ?? 0) - (cat2?.id ?? 0);
            }
            return (cat1?.order ?? 0) - (cat2?.order ?? 0);
        },
        limit: 100,
        transformItems(items) {
            const transform = (items: HierarchicalMenuItem[]): HierarchicalMenuItem[] => {
                return items
                    .map(item => {
                        const category = findCategory(item.value);
                        return category ? {
                            ...item,
                            data: item.data ? transform(item.data) : null,
                            label: category?.name ?? '',
                        } : null
                    })
                    .filter(item => !!item)
            };
            return transform(items);
        }
    }
    const catalogThemesMenuParams: MenuConnectorParams = {
        attribute: 'tags',
        limit: 100,
        sortBy(a, b) {
            return props.catalog!.themes.findIndex(theme => String(theme.productTag.id) === a.name)
                 - props.catalog!.themes.findIndex(theme => String(theme.productTag.id) === b.name);
        },
        transformItems(items) {
            return items
                .map(item => ({
                    ...item,
                    label: props.catalog!.themes.find(theme => String(theme.productTag.id) === item.value)?.label ?? '',
                }))
                .filter(item => item.label)
        }
    }
    const currentRefinementsParams: CurrentRefinementsConnectorParams = {
        excludedAttributes: categoryDropdownFilter ? ['query','tags'] : ['category','subcategory','query','tags'],
    };
    const clearRefinementsParams: ClearRefinementsConnectorParams = {
        excludedAttributes: categoryDropdownFilter ? ['query','tags'] : ['category','subcategory','query','tags'],
    };
    const filterParams: {
        'brand': RefinementListConnectorParams | null,
        'decor': RefinementListConnectorParams | null,
        'material': RefinementListConnectorParams,
        'color': MenuConnectorParams,
        'price': RangeConnectorParams,
    } = {
        'brand': !props.filterBrandId ? { attribute: 'brand', limit: 100, sortBy: ['name:asc'] } : null,
        'decor': props.filterBrandId ? { attribute: 'decor', limit: 100, sortBy: ['name:asc'] } : null,
        'material': { attribute: 'material', limit: 100, sortBy: ['name:asc'] },
        'color': { attribute: 'color', sortBy: ['name:asc', 'count:desc'],
            transformItems(items) {
                return items
                    .map(item => ({ ...item, label: findColor(item.value)?.label ?? '' }))
            },
            limit: 100,
        },
        'price': { attribute: 'price' },
    }
    const sortByParams: SortByConnectorParams = {
        items: [
            { value: indexName, label: 'Recommandés' },
            { value: `${indexName}:price:asc`, label: 'Prix croissant' },
            { value: `${indexName}:price:desc`, label: 'Prix décroissant' },
        ]
    };
    const filtersMenuVisible = ref(false);

    function currentPriceRefinementLabel(item: CurrentRefinementsConnectorParamsItem): string {
        if(item.refinements.length === 2) {
            return `entre ${(item.refinements[0].value as number) / 100}€ et ${(item.refinements[1].value as number) / 100}€`;
        }
        if(item.refinements[0].operator === '>=') {
            return `entre ${(item.refinements[0].value as number) / 100}€ et ${Math.ceil(renderState.value!.range!.price.range.max! / 100)}€`;
        }
        if(item.refinements[0].operator === '<=') {
            return `entre ${Math.floor(renderState.value!.range!.price.range.min! / 100)}€ et ${(item.refinements[0].value as number) / 100}€`
        }
        return '';
    }

    function currentRefinementLabel(refinement: CurrentRefinementsConnectorParamsRefinement) {
        if(refinement.attribute === 'color') {
            return props.productColors.find(color => color.hex_value === refinement.value)?.label ?? '';
        }
        if(refinement.attribute === 'decor') {
            return `Collection : ${refinement.label.charAt(0).toLocaleUpperCase() + refinement.label.slice(1).toLocaleLowerCase()}`;
        }
        if(refinement.attribute === 'category') {
            return findCategory(refinement.value as string)?.name ?? `Catégorie ${refinement.value}`;
        }
        // if(refinement.attribute === 'tags') {
        //     return props.catalog!.themes.find(theme => String(theme.productTag.id) === refinement.value)?.label ?? '';
        // }
        return refinement.label;
    }

    function productItemsRendererQueryParams(state: InfiniteHitsState) {
        return {
            'page': (state.results?.page ?? 0) + 1,
            'catalog_block_id': props.catalogBlock?.id,
            'recipe_id': props.recipe?.id,
        }
    }
    function hasMoreResults(state: InfiniteHitsState) {
        return state.items.length < state.results!.nbHits;
    }

    return {
        ready,
        instantSearchOptions,
        searchParameters,
        infiniteHitsParams,
        categoryMenuParams,
        currentRefinementsParams,
        clearRefinementsParams,
        catalogThemesMenuParams,
        renderState,
        filterInStockForProfileStore,
        filtersMenuVisible,
        filterParams,
        sortByParams,
        currentRefinementLabel,
        currentPriceRefinementLabel,
        productItemsRendererQueryParams: productItemsRendererQueryParams,
        findCategory,
        findColor,
        hasMoreResults,
        onProductItemsLoaded,
    }
}
